// Local SEO data for jaik: King County cities and contractor trades.
// Used to generate city, trade, and city + trade landing pages plus the sitemap.

export const SITE_ORIGIN = "https://jaikmarketing.com";

export type LocalCity = {
  slug: string;
  name: string;
  county: string;
  population: string;
  /** Short positioning line used in hero copy. */
  angle: string;
  /** Two to three sentences of market context unique to this city. */
  market: string;
  /** Neighborhoods and landmarks used for on-page local relevance. */
  areas: string[];
  /** Nearby city slugs used for internal linking. */
  nearby: string[];
  /** Whether this city gets the full trade page set. */
  priority?: boolean;
  zip: string;
};

export const cities: LocalCity[] = [
  {
    slug: "seattle",
    name: "Seattle",
    county: "King County",
    population: "755,000",
    angle: "the most competitive contractor market in Washington",
    market:
      "Seattle homeowners research online before they ever pick up the phone, and most of them compare three or four contractors in the same afternoon. Older housing stock in Ballard, Wallingford and West Seattle keeps demand steady for roofing, remodeling and plumbing work. Winning here comes down to ranking in the map pack and giving people a reason to call you first.",
    areas: ["Ballard", "West Seattle", "Queen Anne", "Capitol Hill", "Wallingford", "Magnolia", "Rainier Valley", "Green Lake"],
    nearby: ["shoreline", "bellevue", "burien", "kirkland"],
    priority: true,
    zip: "98101",
  },
  {
    slug: "bellevue",
    name: "Bellevue",
    county: "King County",
    population: "151,000",
    angle: "high-value projects and homeowners who expect polish",
    market:
      "Bellevue budgets are larger and so are the expectations. Homeowners in Somerset, Bridle Trails and West Bellevue compare finish quality, warranties and reviews before they book. A website that looks as good as your work is the difference between a quote request and a bounce.",
    areas: ["Somerset", "Bridle Trails", "Crossroads", "West Bellevue", "Factoria", "Newport Hills", "Lake Hills"],
    nearby: ["kirkland", "redmond", "sammamish", "issaquah"],
    priority: true,
    zip: "98004",
  },
  {
    slug: "kent",
    name: "Kent",
    county: "King County",
    population: "136,000",
    angle: "steady residential volume across the valley",
    market:
      "Kent runs on volume. Between East Hill, Panther Lake and the valley floor there is constant demand for roofing, plumbing, fencing and exterior work, and most of it starts as a phone search. Fast pages and click-to-call buttons matter more here than long brochure copy.",
    areas: ["East Hill", "Panther Lake", "Kent Valley", "Meridian", "Scenic Hill"],
    nearby: ["auburn", "renton", "federal-way", "puyallup"],
    priority: true,
    zip: "98032",
  },
  {
    slug: "renton",
    name: "Renton",
    county: "King County",
    population: "107,000",
    angle: "fast growth and a wide mix of home ages",
    market:
      "Renton mixes mid-century homes in Highlands with new construction in Kennydale and the Benson area. That range means a contractor can sell both repair work and full replacements, as long as the website makes each service easy to find and easy to request.",
    areas: ["Highlands", "Kennydale", "Talbot Hill", "Benson Hill", "Fairwood"],
    nearby: ["kent", "seattle", "bellevue", "auburn"],
    priority: true,
    zip: "98055",
  },
  {
    slug: "federal-way",
    name: "Federal Way",
    county: "King County",
    population: "101,000",
    angle: "an under-served south end market",
    market:
      "Fewer south end contractors invest in local SEO, which makes Federal Way one of the easier King County markets to rank in. Homes around Twin Lakes, Mirror Lake and Redondo need siding, roofing and exterior cleaning on a regular cycle.",
    areas: ["Twin Lakes", "Mirror Lake", "Redondo", "Steel Lake", "Lakeland"],
    nearby: ["auburn", "kent", "burien", "tacoma"],
    priority: true,
    zip: "98003",
  },
  {
    slug: "kirkland",
    name: "Kirkland",
    county: "King County",
    population: "93,000",
    angle: "waterfront homes and design-driven renovations",
    market:
      "Kirkland homeowners in Juanita, Houghton and Rose Hill hire for aesthetics as much as function. Portfolio galleries, before-and-after photos and clear project timelines do more here than a discount banner.",
    areas: ["Juanita", "Houghton", "Rose Hill", "Totem Lake", "Finn Hill"],
    nearby: ["bellevue", "redmond", "bothell", "seattle"],
    priority: true,
    zip: "98033",
  },
  {
    slug: "auburn",
    name: "Auburn",
    county: "King County",
    population: "88,000",
    angle: "practical homeowners who compare quotes fast",
    market:
      "Auburn callers tend to be ready to book. They want a real price range, a real timeline and proof you have worked in Lea Hill or Lakeland Hills before. A simple, quick-loading site with visible pricing guidance converts better than a polished site with none.",
    areas: ["Lea Hill", "Lakeland Hills", "West Hill", "Downtown Auburn"],
    nearby: ["kent", "federal-way", "puyallup"],
    priority: true,
    zip: "98002",
  },
  {
    slug: "redmond",
    name: "Redmond",
    county: "King County",
    population: "78,000",
    angle: "tech-sector homeowners who judge you by your website",
    market:
      "Redmond buyers expect a site that loads instantly, works on a phone and lets them book without a phone call. Education Hill and Grass Lawn homes are hitting the age where roofs, windows and kitchens all need attention at once.",
    areas: ["Education Hill", "Grass Lawn", "Overlake", "Idylwood", "Union Hill"],
    nearby: ["kirkland", "sammamish", "bellevue", "issaquah"],
    priority: true,
    zip: "98052",
  },
  {
    slug: "sammamish",
    name: "Sammamish",
    county: "King County",
    population: "67,000",
    angle: "large homes and large project budgets",
    market:
      "Sammamish is almost entirely owner-occupied single family homes built in the 1990s and 2000s, which puts the whole city on the same replacement cycle for roofs, decks and exteriors. Reviews and warranty language carry a lot of weight here.",
    areas: ["Klahanie", "Trossachs", "Pine Lake", "Beaver Lake"],
    nearby: ["issaquah", "redmond", "bellevue", "kirkland"],
    priority: true,
    zip: "98074",
  },
  {
    slug: "burien",
    name: "Burien",
    county: "King County",
    population: "52,000",
    angle: "older homes with constant maintenance needs",
    market:
      "Burien and the surrounding shoreline neighborhoods are full of mid-century homes that need siding, roofing and drainage work. Search volume is smaller than Seattle, which means a well-built local page can hold the top spot for a long time.",
    areas: ["Three Tree Point", "Gregory Heights", "Boulevard Park", "Seahurst"],
    nearby: ["seattle", "federal-way", "tacoma"],
    priority: true,
    zip: "98166",
  },
  {
    slug: "shoreline",
    name: "Shoreline",
    county: "King County",
    population: "58,000",
    angle: "light rail growth and steady remodel demand",
    market:
      "Light rail has pushed property values up in Shoreline, and homeowners are investing in the homes they already own. Richmond Beach and Echo Lake see a lot of kitchen, bath and ADU work.",
    areas: ["Richmond Beach", "Echo Lake", "Ridgecrest", "Innis Arden"],
    nearby: ["seattle", "lynnwood", "bothell"],
    zip: "98133",
  },
  {
    slug: "issaquah",
    name: "Issaquah",
    county: "King County",
    population: "40,000",
    angle: "hillside homes with drainage and exterior demands",
    market:
      "Issaquah Highlands and the older downtown core create two very different customer types in one city. Hillside lots mean drainage, retaining walls and gutter work come up constantly.",
    areas: ["Issaquah Highlands", "Squak Mountain", "Talus", "Olde Town"],
    nearby: ["sammamish", "bellevue", "redmond"],
    zip: "98027",
  },
  {
    slug: "bothell",
    name: "Bothell",
    county: "King County",
    population: "49,000",
    angle: "a two-county market with strong remodel activity",
    market:
      "Bothell spans King and Snohomish County lines, so your service area page needs to speak to both. Canyon Park and North Creek homes are in the middle of a heavy renovation cycle.",
    areas: ["Canyon Park", "North Creek", "Queensborough", "Thrashers Corner"],
    nearby: ["kirkland", "shoreline", "lynnwood", "redmond"],
    zip: "98011",
  },
  {
    slug: "tacoma",
    name: "Tacoma",
    county: "Pierce County",
    population: "222,000",
    angle: "the biggest market south of Seattle and far less crowded online",
    market:
      "Tacoma has the housing stock of a much older city and the search competition of a much smaller one. Craftsman homes in the North End and Proctor need roofs, wiring and remodels, while Eastside and South Tacoma run on repair work. Most contractors here still have a site from a decade ago, which makes ranking realistic.",
    areas: ["North End", "Proctor", "Stadium District", "Hilltop", "South Tacoma", "Browns Point"],
    nearby: ["lakewood", "puyallup", "federal-way", "auburn"],
    zip: "98402",
  },
  {
    slug: "everett",
    name: "Everett",
    county: "Snohomish County",
    population: "111,000",
    angle: "a working city with steady repair and replacement demand",
    market:
      "Everett buyers are practical. Between Boeing shift schedules and older homes in Riverside and Bayside, most searches are about getting a job done well at a fair price, not about design inspiration. Clear pricing pages and fast quote forms do the heavy lifting here.",
    areas: ["Riverside", "Bayside", "Silver Lake", "Harbour Pointe line", "North Everett"],
    nearby: ["marysville", "lynnwood", "bothell", "shoreline"],
    zip: "98201",
  },
  {
    slug: "lynnwood",
    name: "Lynnwood",
    county: "Snohomish County",
    population: "42,000",
    angle: "light rail growth pulling in new homeowners",
    market:
      "The light rail extension changed who is buying in Lynnwood. Newer owners are renovating homes built in the seventies and eighties, which means kitchens, baths, windows and panel upgrades. The search volume is climbing faster than the number of contractors targeting it.",
    areas: ["Alderwood", "Meadowdale", "Martha Lake line", "College Place"],
    nearby: ["everett", "shoreline", "bothell", "seattle"],
    zip: "98036",
  },
  {
    slug: "marysville",
    name: "Marysville",
    county: "Snohomish County",
    population: "72,000",
    angle: "suburban volume with very little online competition",
    market:
      "Marysville and the Smokey Point corridor added thousands of homes over the last twenty years and those homes are now hitting their first big repair cycle. Roofing, fencing, gutters and exterior work all sell here, and the local search results are still wide open.",
    areas: ["Smokey Point", "Sunnyside", "Lakewood area", "Downtown Marysville"],
    nearby: ["everett", "lynnwood", "bothell", "seattle"],
    zip: "98270",
  },
  {
    slug: "puyallup",
    name: "Puyallup",
    county: "Pierce County",
    population: "43,000",
    angle: "valley growth and a strong word-of-mouth market",
    market:
      "Puyallup buyers ask their neighbors first and then check you online. That means your reviews, your Google profile and your project photos matter more than clever copy. South Hill keeps adding homes, and the older valley stock keeps needing work.",
    areas: ["South Hill", "Downtown Puyallup", "Sunrise", "Edgewood line"],
    nearby: ["tacoma", "auburn", "kent", "federal-way"],
    zip: "98371",
  },
  {
    slug: "lakewood",
    name: "Lakewood",
    county: "Pierce County",
    population: "64,000",
    angle: "a mix of military families and long-term owners",
    market:
      "Lakewood turns over faster than most Puget Sound cities because of Joint Base Lewis-McChord. Buyers want quick scheduling, straight pricing and proof you show up. Rentals and property managers are a real second audience here.",
    areas: ["Lakewood Towne Center", "Tillicum", "American Lake", "Steilacoom line"],
    nearby: ["tacoma", "puyallup", "olympia", "federal-way"],
    zip: "98499",
  },
  {
    slug: "olympia",
    name: "Olympia",
    county: "Thurston County",
    population: "56,000",
    angle: "a state capital market that rewards a polished, credible site",
    market:
      "Olympia has a lot of state employees, a lot of older homes and a buyer who reads before they call. Longer service pages perform well here, and moisture, moss and roof work are year-round searches thanks to the south sound weather.",
    areas: ["Downtown Olympia", "Eastside", "Westside", "Lacey line", "Tumwater line"],
    nearby: ["lakewood", "tacoma", "puyallup", "federal-way"],
    zip: "98501",
  },
  {
    slug: "bremerton",
    name: "Bremerton",
    county: "Kitsap County",
    population: "44,000",
    angle: "a peninsula market most Seattle agencies ignore",
    market:
      "Bremerton and the surrounding Kitsap communities are separated from Seattle by the ferry, and that keeps a lot of competition out. Shipyard schedules, older housing and heavy weather exposure make roofing, siding and exterior repair steady sellers.",
    areas: ["East Bremerton", "Manette", "West Bremerton", "Silverdale line", "Port Orchard line"],
    nearby: ["seattle", "burien", "tacoma", "shoreline"],
    zip: "98337",
  },

  // ---- Additional King County markets ----
  {
    slug: "mercer-island",
    name: "Mercer Island",
    county: "King County",
    population: "25,000",
    angle: "small city, large budgets, very little local competition",
    market:
      "Mercer Island has some of the highest project budgets in the state packed into a handful of square miles. Buyers here read reviews, check licensing and expect permit experience spelled out. A single well-built city page can own these searches because almost nobody targets the island directly.",
    areas: ["North End", "First Hill", "East Seattle", "Mercerwood", "Island Crest"],
    nearby: ["bellevue", "seattle", "newcastle", "renton"],
    zip: "98040",
  },
  {
    slug: "newcastle",
    name: "Newcastle",
    county: "King County",
    population: "13,000",
    angle: "hillside homes on a twenty-year replacement cycle",
    market:
      "Newcastle is compact, affluent and full of homes built in the late nineties that are all aging into roof, deck and exterior work at the same time. Hillside lots keep drainage and retaining wall work in steady demand.",
    areas: ["China Creek", "Olympus", "Lake Boren", "Coal Creek"],
    nearby: ["renton", "bellevue", "issaquah", "mercer-island"],
    zip: "98059",
  },
  {
    slug: "woodinville",
    name: "Woodinville",
    county: "King County",
    population: "13,000",
    angle: "acreage properties and a strong commercial pocket",
    market:
      "Woodinville mixes wine country acreage with newer subdivisions and a busy commercial district. Outdoor living, fencing, septic-adjacent site work and full remodels all sell here, and the commercial tenants are a second audience most contractor sites ignore.",
    areas: ["Hollywood Hill", "Cottage Lake line", "Tourist District", "Wellington"],
    nearby: ["bothell", "redmond", "kirkland", "monroe"],
    zip: "98072",
  },
  {
    slug: "maple-valley",
    name: "Maple Valley",
    county: "King County",
    population: "28,000",
    angle: "family neighborhoods hitting their first big repair cycle",
    market:
      "Maple Valley grew fast in the 2000s and those homes now need roofs, decks, siding and fences. Buyers here get referrals from neighborhood groups first, so your reviews and your Google profile carry the page.",
    areas: ["Four Corners", "Lake Wilderness", "Summit Place", "Cedar Downs"],
    nearby: ["covington", "renton", "kent", "enumclaw"],
    zip: "98038",
  },
  {
    slug: "covington",
    name: "Covington",
    county: "King County",
    population: "21,000",
    angle: "an easy south King County market to rank in",
    market:
      "Covington sits between Kent and Maple Valley and is served mostly by contractors who never built a page for it. Suburban homes from the nineties keep exterior, gutter and remodel work steady all year.",
    areas: ["Timberlane", "Jenkins Creek", "Covington Estates", "Pipe Lake line"],
    nearby: ["maple-valley", "kent", "auburn", "renton"],
    zip: "98042",
  },
  {
    slug: "enumclaw",
    name: "Enumclaw",
    county: "King County",
    population: "13,000",
    angle: "a rural plateau market that rewards showing up locally",
    market:
      "Enumclaw and the plateau run on reputation. Farm outbuildings, older homes and long driveways mean metal roofing, fencing, concrete and general contracting all sell. Search volume is low but intent is high and competition is almost nonexistent.",
    areas: ["Downtown Enumclaw", "Black Diamond line", "Buckley line", "Cumberland"],
    nearby: ["maple-valley", "auburn", "covington", "puyallup"],
    zip: "98022",
  },
  {
    slug: "seatac",
    name: "SeaTac",
    county: "King County",
    population: "31,000",
    angle: "rental-heavy housing with constant turnover work",
    market:
      "SeaTac has a high share of rentals and small multifamily buildings, so property managers are as important a buyer as homeowners. Turnover painting, flooring, roofing and plumbing repeat all year.",
    areas: ["Angle Lake", "McMicken Heights", "Bow Lake", "Riverton Heights"],
    nearby: ["tukwila", "burien", "des-moines", "kent"],
    zip: "98188",
  },
  {
    slug: "tukwila",
    name: "Tukwila",
    county: "King County",
    population: "22,000",
    angle: "a commercial core with residential pockets around it",
    market:
      "Tukwila is retail and light industrial at the center with older residential neighborhoods on the edges. Commercial tenant improvement work and residential repair work both live here, and each needs its own page to rank.",
    areas: ["Southcenter", "Allentown", "Foster", "Riverton"],
    nearby: ["seatac", "renton", "burien", "kent"],
    zip: "98188",
  },
  {
    slug: "des-moines",
    name: "Des Moines",
    county: "King County",
    population: "33,000",
    angle: "waterfront exposure and older homes that need upkeep",
    market:
      "Des Moines homes take weather straight off the sound, which keeps roofing, siding, gutters and moss treatment in demand. The market is small enough that one strong page can hold the top spot for years.",
    areas: ["Marina District", "Woodmont", "Zenith", "Redondo line"],
    nearby: ["federal-way", "burien", "seatac", "kent"],
    zip: "98198",
  },
  {
    slug: "kenmore",
    name: "Kenmore",
    county: "King County",
    population: "23,000",
    angle: "north end lake homes with remodel money behind them",
    market:
      "Kenmore sits at the top of Lake Washington with a mix of older ramblers and newer builds. Kitchen, bath, deck and ADU work all sell here, and most competitors only target Bothell or Kirkland.",
    areas: ["Inglewood", "Uplake", "Moorlands", "Arrowhead"],
    nearby: ["bothell", "kirkland", "shoreline", "woodinville"],
    zip: "98028",
  },

  // ---- Snohomish County ----
  {
    slug: "edmonds",
    name: "Edmonds",
    county: "Snohomish County",
    population: "43,000",
    angle: "older waterfront-adjacent homes with design-minded owners",
    market:
      "Edmonds homeowners care how the finished work looks from the street. Bowl neighborhoods are full of mid-century homes getting full remodels, while the ridge and Perrinville see steady roofing and siding replacement.",
    areas: ["Edmonds Bowl", "Perrinville", "Westgate", "Seaview", "Firdale"],
    nearby: ["lynnwood", "shoreline", "mountlake-terrace", "everett"],
    zip: "98020",
  },
  {
    slug: "mountlake-terrace",
    name: "Mountlake Terrace",
    county: "Snohomish County",
    population: "23,000",
    angle: "light rail growth in a market few contractors target",
    market:
      "The light rail station changed Mountlake Terrace. Sixties and seventies homes are being updated by newer owners, which means panels, windows, kitchens and baths. Very few contractors have a page written for this city.",
    areas: ["Melody Hill", "Cedar Way", "Terrace Creek", "Lake Ballinger"],
    nearby: ["lynnwood", "edmonds", "shoreline", "bothell"],
    zip: "98043",
  },
  {
    slug: "mukilteo",
    name: "Mukilteo",
    county: "Snohomish County",
    population: "21,000",
    angle: "view homes with high standards and high budgets",
    market:
      "Mukilteo view properties get weather from the water and owners who protect their investment. Roofing, decks, windows and exterior painting come up on a predictable cycle, and quality proof matters more than price here.",
    areas: ["Old Town", "Harbour Pointe", "Japanese Gulch line", "Picnic Point line"],
    nearby: ["everett", "lynnwood", "edmonds", "marysville"],
    zip: "98275",
  },
  {
    slug: "mill-creek",
    name: "Mill Creek",
    county: "Snohomish County",
    population: "21,000",
    angle: "planned neighborhoods on a synchronized replacement cycle",
    market:
      "Mill Creek is mostly planned communities built within a twenty-year window, so entire streets need roofs, siding and fences around the same time. One good job leads to three neighbors calling if your site makes you easy to find again.",
    areas: ["Mill Creek Town Center", "The Country Club", "Silver Firs line", "North Creek line"],
    nearby: ["bothell", "everett", "lynnwood", "snohomish"],
    zip: "98012",
  },
  {
    slug: "snohomish",
    name: "Snohomish",
    county: "Snohomish County",
    population: "10,000",
    angle: "historic homes and rural acreage in one small market",
    market:
      "Snohomish pairs a historic downtown with farm and acreage properties around it. Older homes need careful remodel and restoration work, and rural owners need shops, fencing and metal roofing. Local search here is wide open.",
    areas: ["Historic Downtown", "Pilchuck", "Three Lakes", "Machias"],
    nearby: ["everett", "monroe", "mill-creek", "marysville"],
    zip: "98290",
  },
  {
    slug: "monroe",
    name: "Monroe",
    county: "Snohomish County",
    population: "20,000",
    angle: "a growing valley town most agencies drive past",
    market:
      "Monroe keeps adding homes along the valley while the older stock in town needs steady repair. Buyers here commute, so they research at night and submit forms rather than call, which makes your quote form the whole ballgame.",
    areas: ["Downtown Monroe", "Fryelands", "Woods Creek", "Tjerne Place"],
    nearby: ["snohomish", "everett", "woodinville", "mill-creek"],
    zip: "98272",
  },
  {
    slug: "arlington",
    name: "Arlington",
    county: "Snohomish County",
    population: "21,000",
    angle: "north county growth with almost no online competition",
    market:
      "Arlington and the Smokey Point corridor keep expanding with new subdivisions and shop buildings. Roofing, concrete, fencing and general contracting sell well and the search results are still easy to move into.",
    areas: ["Smokey Point", "Downtown Arlington", "Gleneagle", "Bryant line"],
    nearby: ["marysville", "everett", "mount-vernon", "mukilteo"],
    zip: "98223",
  },

  // ---- Pierce County ----
  {
    slug: "gig-harbor",
    name: "Gig Harbor",
    county: "Pierce County",
    population: "12,000",
    angle: "waterfront properties and premium project budgets",
    market:
      "Gig Harbor buyers spend more and expect more. Waterfront and view homes need decks, roofing, windows and outdoor living work, and owners here compare portfolios closely before they call.",
    areas: ["Downtown Harbor", "Canterwood", "Rosedale", "Artondale", "Fox Island line"],
    nearby: ["tacoma", "university-place", "bremerton", "port-orchard"],
    zip: "98335",
  },
  {
    slug: "university-place",
    name: "University Place",
    county: "Pierce County",
    population: "35,000",
    angle: "established neighborhoods with steady remodel spend",
    market:
      "University Place is settled, owner-occupied and full of homes from the sixties through the nineties. Kitchens, baths, roofs and exteriors all turn over here, and the market is small enough to dominate with one page.",
    areas: ["Chambers Bay", "Sunset Terrace", "Day Island line", "Bridgeport"],
    nearby: ["tacoma", "lakewood", "gig-harbor", "puyallup"],
    zip: "98466",
  },
  {
    slug: "bonney-lake",
    name: "Bonney Lake",
    county: "Pierce County",
    population: "23,000",
    angle: "plateau growth with a young homeowner base",
    market:
      "Bonney Lake and the surrounding plateau filled in quickly with family housing. Decks, fences, concrete and exterior work sell constantly, and buyers here trust neighborhood recommendations backed by a real website.",
    areas: ["Downtown Bonney Lake", "Tehaleh line", "Lake Tapps", "Sumner line"],
    nearby: ["sumner", "puyallup", "auburn", "enumclaw"],
    zip: "98391",
  },
  {
    slug: "sumner",
    name: "Sumner",
    county: "Pierce County",
    population: "10,000",
    angle: "a small valley town with real commercial demand",
    market:
      "Sumner has a historic residential core and a large warehouse district beside it. That means both homeowner repair work and commercial maintenance contracts, and almost no local contractor site is built to capture both.",
    areas: ["Old Town Sumner", "Elhi Hill", "Valley Industrial", "Orting line"],
    nearby: ["puyallup", "bonney-lake", "auburn", "tacoma"],
    zip: "98390",
  },

  // ---- Kitsap County ----
  {
    slug: "silverdale",
    name: "Silverdale",
    county: "Kitsap County",
    population: "22,000",
    angle: "the commercial hub of the peninsula",
    market:
      "Silverdale is where the rest of Kitsap shops, which makes it the strongest search market on the peninsula. Homes around Clear Creek and Ridgetop need roofing, gutters and moss work every year thanks to the weather.",
    areas: ["Ridgetop", "Clear Creek", "Old Town Silverdale", "Central Valley"],
    nearby: ["bremerton", "poulsbo", "port-orchard", "bainbridge-island"],
    zip: "98383",
  },
  {
    slug: "poulsbo",
    name: "Poulsbo",
    county: "Kitsap County",
    population: "12,000",
    angle: "a tourism town with steady residential upkeep work",
    market:
      "Poulsbo mixes a walkable downtown with wooded residential lots. Moss, gutters, decks and siding need attention constantly, and commercial storefronts need their own maintenance. Competition online is thin.",
    areas: ["Historic Front Street", "Lemolo", "Viking Way", "Keyport line"],
    nearby: ["silverdale", "bainbridge-island", "bremerton", "port-orchard"],
    zip: "98370",
  },
  {
    slug: "bainbridge-island",
    name: "Bainbridge Island",
    county: "Kitsap County",
    population: "25,000",
    angle: "high budgets and buyers who research carefully",
    market:
      "Bainbridge homeowners commute to Seattle and spend accordingly. Remodels, additions, decks and exterior work all carry strong budgets, and buyers here read your whole site before they reach out.",
    areas: ["Winslow", "Rolling Bay", "Lynwood Center", "Island Center", "Port Madison"],
    nearby: ["poulsbo", "silverdale", "bremerton", "seattle"],
    zip: "98110",
  },
  {
    slug: "port-orchard",
    name: "Port Orchard",
    county: "Kitsap County",
    population: "16,000",
    angle: "affordable housing stock that always needs work",
    market:
      "Port Orchard and South Kitsap are full of older, weather-exposed homes on wooded lots. Roofing, siding, decks and pressure washing are year-round sellers and very few contractors here rank on purpose.",
    areas: ["Downtown Port Orchard", "Manchester", "McCormick Woods", "Bethel"],
    nearby: ["bremerton", "silverdale", "gig-harbor", "tacoma"],
    zip: "98366",
  },

  // ---- Thurston County ----
  {
    slug: "lacey",
    name: "Lacey",
    county: "Thurston County",
    population: "58,000",
    angle: "fast suburban growth beside the capital",
    market:
      "Lacey has grown faster than Olympia and carries a younger homeowner base with newer housing. Fencing, decks, concrete and exterior work sell steadily, and the search results have not caught up to the population.",
    areas: ["Hawks Prairie", "Meridian Campus", "Woodland Creek", "College Street"],
    nearby: ["olympia", "tumwater", "lakewood", "tacoma"],
    zip: "98503",
  },
  {
    slug: "tumwater",
    name: "Tumwater",
    county: "Thurston County",
    population: "26,000",
    angle: "a steady south sound market with low competition",
    market:
      "Tumwater homeowners are practical and long-term. Roofs, gutters, moss treatment and remodel work all repeat here, and because most agencies stop at Olympia, a Tumwater page ranks quickly.",
    areas: ["Littlerock Road", "Black Lake", "Bush Prairie", "Old Town Tumwater"],
    nearby: ["olympia", "lacey", "lakewood", "tacoma"],
    zip: "98501",
  },

  // ---- North Sound ----
  {
    slug: "bellingham",
    name: "Bellingham",
    county: "Whatcom County",
    population: "93,000",
    angle: "the largest market north of Everett and still winnable",
    market:
      "Bellingham has old housing stock, a large rental base and heavy weather exposure. Roofing, gutters, moisture repair and remodel work all sell, and the contractor sites competing here are mostly outdated.",
    areas: ["Fairhaven", "Columbia", "Sunnyland", "Barkley", "South Hill", "Birchwood"],
    nearby: ["ferndale", "mount-vernon", "anacortes", "everett"],
    zip: "98225",
  },
  {
    slug: "ferndale",
    name: "Ferndale",
    county: "Whatcom County",
    population: "16,000",
    angle: "a growing small city almost nobody targets online",
    market:
      "Ferndale keeps adding housing while the refinery and trades economy keeps incomes steady. Fencing, concrete, roofing and shop buildings sell here and local search competition is close to zero.",
    areas: ["Downtown Ferndale", "Church Road", "Vista", "Custer line"],
    nearby: ["bellingham", "mount-vernon", "anacortes", "arlington"],
    zip: "98248",
  },
  {
    slug: "mount-vernon",
    name: "Mount Vernon",
    county: "Skagit County",
    population: "36,000",
    angle: "the Skagit Valley hub with a wide service radius",
    market:
      "Mount Vernon serves the whole Skagit Valley, so a site here pulls calls from Burlington, Sedro-Woolley and the farms in between. Flood-prone valley homes, older downtown stock and rural acreage all need different work.",
    areas: ["Downtown Mount Vernon", "Little Mountain", "College Way", "Burlington line"],
    nearby: ["anacortes", "bellingham", "oak-harbor", "arlington"],
    zip: "98273",
  },
  {
    slug: "anacortes",
    name: "Anacortes",
    county: "Skagit County",
    population: "18,000",
    angle: "saltwater exposure and a wealthier retiree base",
    market:
      "Anacortes homes take salt air and wind all year, which keeps roofing, siding, painting and deck work in constant rotation. Owners tend to be older, financially comfortable and willing to pay for work done right the first time.",
    areas: ["Old Town", "Skyline", "Fidalgo Island", "Guemes Channel"],
    nearby: ["mount-vernon", "oak-harbor", "bellingham", "ferndale"],
    zip: "98221",
  },
  {
    slug: "oak-harbor",
    name: "Oak Harbor",
    county: "Island County",
    population: "24,000",
    angle: "a military market with fast turnover and quick decisions",
    market:
      "Whidbey Island Naval Air Station keeps Oak Harbor moving. Buyers relocate often, which means fast scheduling, clear pricing and rental turnover work. Weather exposure keeps exterior services in demand year-round.",
    areas: ["Downtown Oak Harbor", "Crescent Harbor", "Rocky Point", "Coupeville line"],
    nearby: ["anacortes", "mount-vernon", "marysville", "everett"],
    zip: "98277",
  },

  // ---- Southwest Washington and the coast ----
  {
    slug: "vancouver",
    name: "Vancouver",
    county: "Clark County",
    population: "196,000",
    angle: "the biggest western Washington market outside Puget Sound",
    market:
      "Vancouver behaves like a Portland suburb with Washington licensing rules. Rapid growth in east Vancouver and Salmon Creek keeps new construction and remodel demand high, while older neighborhoods near downtown need repair work.",
    areas: ["Salmon Creek", "Felida", "Cascade Park", "Hazel Dell", "Fishers Landing", "Downtown Vancouver"],
    nearby: ["camas", "battle-ground", "longview", "olympia"],
    zip: "98660",
  },
  {
    slug: "camas",
    name: "Camas",
    county: "Clark County",
    population: "27,000",
    angle: "high incomes and newer homes with premium upgrades",
    market:
      "Camas has some of the strongest household incomes in southwest Washington. Owners invest in outdoor living, finish upgrades and energy work rather than basic repair, so the site has to sell craftsmanship.",
    areas: ["Prune Hill", "Grass Valley", "Downtown Camas", "Lacamas Lake"],
    nearby: ["vancouver", "battle-ground", "longview"],
    zip: "98607",
  },
  {
    slug: "battle-ground",
    name: "Battle Ground",
    county: "Clark County",
    population: "21,000",
    angle: "semi-rural growth with wide-open search results",
    market:
      "Battle Ground blends subdivisions with acreage properties north of Vancouver. Shops, fencing, concrete, roofing and septic-adjacent site work all sell, and hardly any contractor here has a page built for the city.",
    areas: ["Downtown Battle Ground", "Meadow Glade", "Brush Prairie", "Hockinson line"],
    nearby: ["vancouver", "camas", "longview"],
    zip: "98604",
  },
  {
    slug: "longview",
    name: "Longview",
    county: "Cowlitz County",
    population: "38,000",
    angle: "an older industrial city with cheap, easy rankings",
    market:
      "Longview and Kelso have older housing, a working-class buyer and very little digital competition. Roofing, plumbing, siding and repair work dominate searches, and a modern site stands out immediately here.",
    areas: ["Old West Side", "Highlands", "St. Helens", "Kelso line"],
    nearby: ["vancouver", "centralia", "olympia", "battle-ground"],
    zip: "98632",
  },
  {
    slug: "centralia",
    name: "Centralia",
    county: "Lewis County",
    population: "18,000",
    angle: "an I-5 corridor market between two metros",
    market:
      "Centralia and Chehalis sit halfway between Olympia and Portland, which means contractors from both directions pass through but nobody owns the search results. Older homes need roofing, wiring and moisture repair.",
    areas: ["Downtown Centralia", "Edison", "Chehalis line", "Fords Prairie"],
    nearby: ["olympia", "tumwater", "longview", "aberdeen"],
    zip: "98531",
  },
  {
    slug: "aberdeen",
    name: "Aberdeen",
    county: "Grays Harbor County",
    population: "17,000",
    angle: "coastal weather that keeps exterior work constant",
    market:
      "Aberdeen and Hoquiam take rain and wind most of the year, so roofing, siding, gutters, moss treatment and moisture repair never slow down. Housing is older and online competition is minimal.",
    areas: ["Downtown Aberdeen", "South Aberdeen", "Hoquiam line", "Cosmopolis"],
    nearby: ["olympia", "centralia", "tumwater", "lakewood"],
    zip: "98520",
  },
  {
    slug: "port-angeles",
    name: "Port Angeles",
    county: "Clallam County",
    population: "20,000",
    angle: "an isolated peninsula market with no real competition",
    market:
      "Port Angeles is far enough from Seattle that regional contractors rarely bid here. Older homes, coastal weather and a retiree-heavy ownership base keep roofing, siding, decks and repair work steady all year.",
    areas: ["Downtown Port Angeles", "Ediz Hook line", "West Side", "Sequim line"],
    nearby: ["poulsbo", "silverdale", "bremerton", "bainbridge-island"],
    zip: "98362",
  },
];

/** Cities grouped by county for navigation and internal linking. */
export const citiesByCounty = (): { county: string; cities: LocalCity[] }[] => {
  const order: string[] = [];
  const map = new Map<string, LocalCity[]>();
  for (const c of cities) {
    if (!map.has(c.county)) {
      map.set(c.county, []);
      order.push(c.county);
    }
    map.get(c.county)!.push(c);
  }
  return order.map((county) => ({ county, cities: map.get(county)! }));
};

export type Trade = {
  slug: string;
  /** Plural business name, e.g. "roofing companies". */
  business: string;
  /** Short label, e.g. "Roofing". */
  label: string;
  /** Keyword-style phrase, e.g. "roofing contractor website". */
  keyword: string;
  intro: string;
  /** Pages a site in this trade usually needs. */
  pages: string[];
  /** What the buyer is actually deciding. */
  buyer: string;
  faqs: { q: string; a: string }[];
  /**
   * Exact search phrase this page targets, e.g. "Roofing Website Design".
   * Set on the trades with real search demand so the title and H1 match
   * what people actually type instead of an invented internal label.
   */
  designTerm?: string;
  /** Meta description written for the targeted phrase. */
  seoDescription?: string;
  /** Extra body paragraphs giving the page real depth. */
  deepDive?: { heading: string; paragraphs: string[] }[];
};

export const trades: Trade[] = [
  {
    slug: "roofing",
    label: "Roofing",
    business: "roofing companies",
    keyword: "roofing contractor website",
    intro:
      "Roof searches are urgent. Someone with a leak is going to call one of the first three companies they see, so your site has to load fast, prove you are local and put the phone number where a thumb already is.",
    pages: ["Roof replacement", "Roof repair", "Metal roofing", "Storm and leak response", "Financing", "Project gallery"],
    buyer: "Homeowners comparing replacement quotes and looking for proof the crew is insured, local and reviewed.",
    faqs: [
      { q: "How fast can a roofing site start bringing in leads?", a: "Call tracking usually shows the first form fills within two to four weeks of launch. Ranking for city and roof-type terms builds over three to six months of consistent SEO work." },
      { q: "Do I need separate pages for each roof type?", a: "Yes. Metal, asphalt and flat roof buyers search differently and convert differently. Separate pages let each one rank and speak to that buyer." },
    ],
    designTerm: "Roofing Web Design",
    seoDescription:
      "Roofing web design and local SEO for Washington roofers. Roof type pages, storm response, financing and quote forms built to turn searches into calls.",
    deepDive: [
      {
        heading: "What a roofing website has to do differently",
        paragraphs: [
          "Roofing is split between two buyers who behave nothing alike. One has water coming through a ceiling right now and will call whoever answers. The other is getting three bids on a twenty year old roof and will spend two weeks reading. A single page cannot serve both, which is why most roofing sites underperform: they are written for the second buyer and lose the first one in the time it takes to find a phone number.",
          "The build handles that with a permanent call bar for the emergency buyer and separate depth pages for the replacement buyer. Roof repair, full replacement, metal, torch down and storm damage each get their own page, because those are separate searches with separate prices and separate objections.",
        ],
      },
      {
        heading: "Ranking a roofer in Washington specifically",
        paragraphs: [
          "Moss, moisture and shortened shingle life are real ranking angles here that a template site from a national vendor will never mention. Pages about moss treatment, gutter and roof drainage, and when a Pacific Northwest roof is actually done pull steady search traffic and give homeowners a reason to trust that you work in this climate.",
          "On top of that, every city you serve gets its own page with real local detail. Ten honest city pages beat fifty copy-paste ones, and Google has gotten very good at telling the difference.",
        ],
      },
    ],
  },
  {
    slug: "plumbing",
    label: "Plumbing",
    business: "plumbing companies",
    keyword: "plumber website",
    intro:
      "Plumbing is a speed business. Emergency searches convert in seconds, so the site is built around one-tap calling, service area clarity and same-day availability messaging.",
    pages: ["Emergency plumbing", "Water heaters", "Drain cleaning", "Repiping", "Remodel plumbing", "Service areas"],
    buyer: "People with an active problem who need to know you can be there today.",
    faqs: [
      { q: "What matters most on a plumbing website?", a: "Page speed and a call button that never scrolls away. Most plumbing leads come from a phone within a minute of landing on the page." },
      { q: "Should emergency and scheduled work share a page?", a: "No. Emergency buyers want availability, scheduled remodel buyers want pricing and photos. Splitting them raises conversion on both." },
    ],
    designTerm: "Plumber Web Design",
    seoDescription:
      "Plumber web design and local SEO in Washington. Emergency call handling, water heater and drain pages, service area coverage and fast mobile pages.",
    deepDive: [
      {
        heading: "Plumbing sites are won on the first ten seconds",
        paragraphs: [
          "Almost nobody reads a plumbing website. They search, they glance, they tap the number. So the build treats everything above the fold as the entire sales pitch: what you do, the towns you cover, whether you can come today, and a call button that follows them down the page.",
          "Everything slower lives below that. Water heaters, repiping, drain and sewer work, and remodel plumbing each get a real page, because those buyers do research and do compare prices, and those are the jobs with margin in them.",
        ],
      },
      {
        heading: "Getting found for emergency searches",
        paragraphs: [
          "Emergency plumbing terms are dominated by whoever Google trusts locally, which comes down to your Google Business Profile, your reviews, and having a page that genuinely matches the search. A page titled emergency plumber in your city, with your actual hours and response window on it, outranks a generic services page every time.",
          "I write and maintain those pages for every town you cover, keep the profile current, and answer reviews in your voice so the listing stays active.",
        ],
      },
    ],
  },
  {
    slug: "hvac",
    label: "HVAC",
    business: "HVAC contractors",
    keyword: "HVAC contractor website",
    intro:
      "HVAC demand swings with the weather. The site needs seasonal landing pages, financing details and maintenance-plan signup so the summer AC rush turns into year-round revenue.",
    pages: ["AC installation", "Furnace repair", "Heat pumps", "Maintenance plans", "Financing", "Rebates"],
    buyer: "Homeowners deciding between repair and replacement, usually during a heat wave or cold snap.",
    faqs: [
      { q: "Do heat pump rebates belong on the site?", a: "Yes. Washington rebate and tax credit pages pull in high-intent search traffic and give your sales conversation a head start." },
      { q: "How do I keep leads coming in the shoulder seasons?", a: "Maintenance plan pages and indoor air quality content fill the gap between heating and cooling season." },
    ],
    designTerm: "HVAC Web Design",
    seoDescription:
      "HVAC web design and local SEO for Washington heating and cooling companies. Heat pump, furnace and AC pages, rebate content and maintenance plan signups.",
    deepDive: [
      {
        heading: "Built around the heat pump shift",
        paragraphs: [
          "Washington is in the middle of a heat pump conversion wave, and the search behavior has moved with it. Homeowners are searching for heat pump cost, heat pump versus furnace, and rebate eligibility long before they search for an installer. An HVAC site that answers those questions gets into the conversation months earlier than one that only lists services.",
          "So the build includes rebate and incentive pages, an honest repair-or-replace comparison, and equipment pages that name the systems you actually install rather than a generic product grid.",
        ],
      },
      {
        heading: "Smoothing out the seasonal swings",
        paragraphs: [
          "HVAC revenue spikes in the first cold snap and the first hot week, then falls off a cliff. Maintenance plan pages, indoor air quality content and duct work pages carry search traffic through the quiet months and turn one-time customers into recurring ones.",
          "Financing gets its own page too. A furnace replacement is a big unplanned expense, and the companies that explain payment options up front lose fewer quotes to sticker shock.",
        ],
      },
    ],
  },
  {
    slug: "electrical",
    label: "Electrical",
    business: "electricians",
    keyword: "electrician website",
    intro:
      "Electrical buyers are checking licensing before anything else. Your license number, insurance and permit experience belong above the fold, followed by the specific work you want more of.",
    pages: ["Panel upgrades", "EV charger installation", "Rewiring", "Lighting", "Generators", "Commercial service"],
    buyer: "Homeowners upgrading panels or adding EV chargers, plus general contractors looking for a reliable sub.",
    faqs: [
      { q: "Is EV charger work worth its own page?", a: "It is one of the fastest growing residential electrical searches in King County and almost nobody targets it by city." },
      { q: "Where should my license number go?", a: "In the header or hero and in the footer of every page. It reduces hesitation and helps local search relevance." },
    ],
    designTerm: "Electrician Web Design",
    seoDescription:
      "Electrician web design and local SEO in Washington. Panel upgrade, EV charger and rewiring pages, license details up front and quote forms that qualify leads.",
  },
  {
    slug: "remodeling",
    label: "Remodeling",
    business: "remodeling contractors",
    keyword: "remodeling contractor website",
    intro:
      "Remodel buyers take weeks to decide, so the site works like a portfolio and a trust builder. Big photography, real project stories, clear budget ranges and a low-pressure consultation request.",
    pages: ["Kitchen remodels", "Bathroom remodels", "Whole home", "ADUs and additions", "Process", "Portfolio"],
    buyer: "Homeowners planning a five-figure project who need to trust you before they call.",
    faqs: [
      { q: "Should I publish price ranges?", a: "Ranges, yes. They filter out mismatched budgets and increase the quality of the consultations you take." },
      { q: "How many project photos do I need?", a: "Enough for eight to twelve complete projects with before and after shots. Depth beats volume." },
    ],
    designTerm: "Remodeling Contractor Web Design",
    seoDescription:
      "Remodeling contractor web design and local SEO in Washington. Kitchen, bath and addition pages, project galleries and budget ranges that filter out mismatched leads.",
  },
  {
    slug: "landscaping",
    label: "Landscaping",
    business: "landscaping companies",
    keyword: "landscaping company website",
    intro:
      "Landscaping sells visually and seasonally. The site leads with transformation photos, splits maintenance from design and build, and makes recurring service easy to sign up for.",
    pages: ["Lawn maintenance", "Landscape design", "Hardscape and patios", "Retaining walls", "Irrigation", "Gallery"],
    buyer: "Homeowners choosing between a one-time project and an ongoing maintenance contract.",
    faqs: [
      { q: "Should maintenance and design share a site?", a: "Same site, separate paths. Maintenance buyers want price and schedule, design buyers want inspiration." },
      { q: "How do I handle the slow season?", a: "Cleanup, pruning and hardscape pages carry search traffic through the winter months." },
    ],
    designTerm: "Landscaping Web Design",
    seoDescription:
      "Landscaping web design and local SEO in Washington. Maintenance and design build split properly, transformation galleries and signup forms for recurring service.",
  },
  {
    slug: "pressure-washing",
    label: "Pressure washing",
    business: "pressure washing companies",
    keyword: "pressure washing website",
    intro:
      "Exterior cleaning sells on before-and-after proof and easy quoting. The site pairs strong photo pairs with a short instant-quote form so people book without a site visit.",
    pages: ["House washing", "Roof and moss treatment", "Driveways and concrete", "Gutter cleaning", "Decks and fences", "Commercial"],
    buyer: "Homeowners who already know they want it done and are choosing on price, proof and availability.",
    faqs: [
      { q: "Do before-and-after photos really matter?", a: "They are the single highest converting element on an exterior cleaning site. Real job photos beat stock every time." },
      { q: "Should I show pricing?", a: "A starting price by home size removes the biggest objection and cuts down on tire-kicker calls." },
    ],
    designTerm: "Pressure Washing Web Design",
    seoDescription:
      "Pressure washing web design and local SEO in Washington. Before and after proof, roof and moss treatment pages and instant quote forms people finish on a phone.",
  },
  {
    slug: "painting",
    label: "Painting",
    business: "painting contractors",
    keyword: "painting contractor website",
    intro:
      "Painting buyers compare three bids. Your site wins by showing prep process, product lines and finished work in homes that look like theirs.",
    pages: ["Interior painting", "Exterior painting", "Cabinet refinishing", "Commercial painting", "Prep process", "Gallery"],
    buyer: "Homeowners weighing bids who want to know how carefully you prep and protect.",
    faqs: [
      { q: "What separates a painting site that converts?", a: "A clear prep and protection section. It is the thing that justifies not being the cheapest bid." },
      { q: "Is cabinet refinishing worth its own page?", a: "Yes. It is a high-margin service with its own search demand and a very different buyer." },
    ],
    designTerm: "Painting Contractor Web Design",
    seoDescription:
      "Painting contractor web design and local SEO in Washington. Interior, exterior and cabinet refinishing pages, prep process detail and galleries that win bids.",
  },
  {
    slug: "fencing",
    label: "Fencing",
    business: "fencing contractors",
    keyword: "fence company website",
    intro:
      "Fence buyers shop by material and by the foot. The site organizes services by fence type, shows completed installs and gives a per-foot starting range to qualify leads.",
    pages: ["Cedar fencing", "Vinyl fencing", "Chain link", "Gates and automation", "Repairs", "Gallery"],
    buyer: "Homeowners pricing a specific fence type and length, often with a pet or privacy driver.",
    faqs: [
      { q: "How specific should fence pages be?", a: "One page per material. Cedar, vinyl and chain link buyers use different search terms and expect different prices." },
      { q: "Do gate pages matter?", a: "Automated gate work is high ticket and lightly competed in King County searches." },
    ],
    designTerm: "Fence Company Web Design",
    seoDescription:
      "Fence company web design and local SEO in Washington. Cedar, vinyl and chain link pages, gate and automation work and per foot ranges that qualify callers.",
  },
  {
    slug: "concrete",
    label: "Concrete",
    business: "concrete contractors",
    keyword: "concrete contractor website",
    intro:
      "Concrete work is priced by scope and sold with photos. Driveways, patios and flatwork each get their own page, with finish options shown clearly.",
    pages: ["Driveways", "Patios", "Walkways", "Stamped and decorative", "Foundations", "Removal and replacement"],
    buyer: "Homeowners replacing failing flatwork and general contractors sourcing a reliable pour crew.",
    faqs: [
      { q: "What should a concrete site show first?", a: "Finished driveways and patios in local neighborhoods, with the finish type labeled." },
      { q: "Do I need a commercial page?", a: "If you take commercial work, yes. It is a separate buyer with separate search behavior." },
    ],
    designTerm: "Concrete Contractor Web Design",
    seoDescription:
      "Concrete contractor web design and local SEO in Washington. Driveway, patio and flatwork pages, finish options shown clearly and forms built for scoped quotes.",
  },
  {
    slug: "flooring",
    label: "Flooring",
    business: "flooring companies",
    keyword: "flooring company website",
    intro:
      "Flooring is chosen by material first. The site is organized by product with room-by-room photos and a square footage estimator that starts the conversation.",
    pages: ["Hardwood", "Luxury vinyl plank", "Tile", "Carpet", "Refinishing", "Room gallery"],
    buyer: "Homeowners mid-project who already know roughly what material they want.",
    faqs: [
      { q: "Should flooring pages be by material or by room?", a: "By material for search, with room galleries inside each page for browsing." },
      { q: "Does an estimator help?", a: "A simple square footage calculator lifts form completion because it gives people something before it asks for something." },
    ],
    designTerm: "Flooring Web Design",
    seoDescription:
      "Flooring web design and local SEO in Washington. Hardwood, vinyl plank, tile and carpet pages, room galleries and a square footage estimator that starts quotes.",
  },
  {
    slug: "general-contracting",
    label: "General contracting",
    business: "general contractors",
    keyword: "general contractor website",
    intro:
      "General contractors sell range and reliability. The site has to make a broad service list feel organized instead of scattered, with one strong page per service line.",
    pages: ["Home additions", "Structural repair", "Decks and outdoor living", "Tenant improvements", "Permitting", "Portfolio"],
    buyer: "Homeowners and property owners with a complex project who want one accountable point of contact.",
    faqs: [
      { q: "How do I stop looking like a jack of all trades?", a: "Lead with two or three signature services and put the rest on a supporting services page." },
      { q: "Does permitting experience matter online?", a: "In King County it matters a lot. A short permitting page reassures buyers on Mercer Island and in Seattle especially." },
    ],
    designTerm: "General Contractor Web Design and SEO",
    seoDescription:
      "General contractor web design and SEO in Washington. Service line pages, permitting and process content, portfolio galleries and quote forms that qualify leads.",
    deepDive: [
      {
        heading: "The general contractor ranking problem",
        paragraphs: [
          "General contractors have the hardest SEO position of any trade, because the thing you are best at, doing everything, is the thing search engines reward least. A page that lists twenty services ranks for none of them. A page about home additions in your city ranks for home additions in your city.",
          "The fix is structure, not more content. Two or three signature service lines get full pages with their own photos, pricing guidance and process. Everything else sits on a supporting services page so it stays findable without diluting the pages that earn the work.",
        ],
      },
      {
        heading: "What actually closes a general contracting lead",
        paragraphs: [
          "These are the slowest sales in the trades. Someone planning an addition or a structural repair is comparing you against two other bids over several weeks, and they are looking for reasons to eliminate people. Permitting experience, a written process, realistic budget ranges and eight to twelve complete project stories do more than any headline.",
          "Permits matter more here than most contractors expect. In Seattle, on Mercer Island and across King County, a short page explaining how you handle permitting removes the single biggest worry a homeowner has about a large project.",
        ],
      },
    ],
  },
];

export const cityBySlug = (slug?: string) => cities.find((c) => c.slug === slug);
export const tradeBySlug = (slug?: string) => trades.find((t) => t.slug === slug);

export const priorityCities = cities.filter((c) => c.priority);

export const cityPath = (city: LocalCity | string) =>
  `/contractor-websites/${typeof city === "string" ? city : city.slug}`;

export const tradePath = (trade: Trade | string) =>
  `/contractor-websites/${typeof trade === "string" ? trade : trade.slug}-contractors`;

/** Every indexable local landing page path: one page per city, one per trade. */
export const allLocalPaths = (): string[] => [
  ...cities.map((c) => cityPath(c)),
  ...trades.map((t) => tradePath(t)),
];
